import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { TrendingUp, Users, Rocket, Copy, Check, Loader2, PlayCircle } from "lucide-react";

/**
 * Admin › Demo Funnel — the acquisition dashboard for the demo × First 100
 * Closers loop.
 *
 * Shows demos-this-week → signups → First Closers, broken down by ref code
 * so you can see which affiliates / channels are actually converting.
 *
 * Backed by `GET /api/admin/demo-funnel?days=N`.
 */
export default function AdminDemoFunnel() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState("");
  const [origin, setOrigin] = useState("");

  useEffect(() => {
    // Resolve production origin once so we can build shareable /demo?ref links.
    setOrigin(typeof window !== "undefined" ? window.location.origin : "");
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/demo-funnel?days=${days}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const totals = data?.totals || {};
  const chartMax = useMemo(() => {
    const arr = data?.demos_by_day || [];
    return Math.max(1, ...arr.map(d => d.count));
  }, [data]);

  const copyLink = async (ref) => {
    const url = ref === "DIRECT" ? `${origin}/demo` : `${origin}/demo?ref=${ref}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(ref);
      setTimeout(() => setCopied(""), 1400);
    } catch { /* ignore */ }
  };

  const fmtPct = (v) => v == null ? "—" : `${Math.round(v * 100)}%`;

  return (
    <div className="space-y-6" data-testid="admin-demo-funnel">
      {/* Header */}
      <div className="card p-5 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-amber-700 text-white flex items-center justify-center">
            <TrendingUp size={20} />
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
              Acquisition
            </div>
            <h2 className="font-display font-black text-2xl tracking-tight text-slate-900">
              Demo → First Closer Funnel
            </h2>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2" data-testid="funnel-range-toggles">
          {[7, 14, 30, 90].map(n => (
            <button
              key={n}
              type="button"
              onClick={() => setDays(n)}
              className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider transition-colors ${
                days === n
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
              data-testid={`funnel-range-${n}d`}
            >
              {n}d
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Kpi
          icon={PlayCircle}
          tint="amber"
          label="Demos"
          value={loading ? "…" : (totals.demos ?? 0)}
          sub={`Last ${days} days`}
          testid="kpi-demos"
        />
        <Kpi
          icon={Users}
          tint="sky"
          label="Signups from demos"
          value={loading ? "…" : (totals.signups ?? 0)}
          sub={`${fmtPct(totals.signup_rate)} of demos converted`}
          testid="kpi-signups"
        />
        <Kpi
          icon={Rocket}
          tint="emerald"
          label="First Closers earned"
          value={loading ? "…" : (totals.first_closers ?? 0)}
          sub={`${fmtPct(totals.first_closer_rate)} of signups → founder`}
          testid="kpi-first-closers"
        />
      </div>

      {/* Sparkline: demos by day */}
      <div className="card p-5" data-testid="funnel-sparkline">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg text-slate-900">
            Demos per day
          </h3>
          <div className="text-xs text-slate-500">Peak: {chartMax}</div>
        </div>
        <div className="flex items-end gap-1 h-32">
          {(data?.demos_by_day || []).map((d) => {
            const h = Math.max(2, (d.count / chartMax) * 100);
            return (
              <div
                key={d.day}
                className="flex-1 group relative"
                title={`${d.day} — ${d.count} demo${d.count === 1 ? "" : "s"}`}
              >
                <div
                  className="w-full rounded-t transition-colors"
                  style={{
                    height: `${h}%`,
                    background: d.count > 0
                      ? "linear-gradient(180deg, #F59E0B 0%, #D97706 100%)"
                      : "#F1F5F9",
                  }}
                />
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-[10px] font-bold text-slate-700 bg-white shadow-md rounded px-1.5 py-0.5 whitespace-nowrap pointer-events-none">
                  {d.count}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between text-[10px] text-slate-400 mt-2">
          <span>{data?.demos_by_day?.[0]?.day || ""}</span>
          <span>{data?.demos_by_day?.[data?.demos_by_day?.length - 1]?.day || ""}</span>
        </div>
      </div>

      {/* Top refs table */}
      <div className="card p-5" data-testid="funnel-top-refs">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg text-slate-900">
            Top ref codes
          </h3>
          <div className="text-xs text-slate-500">
            Tap a code to copy its shareable /demo link
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        )}

        {!loading && (!data?.top_refs || data.top_refs.length === 0) && (
          <div className="text-center py-10 text-slate-500 text-sm">
            No demo activity in the last {days} days yet.
            <div className="mt-2 text-xs">
              Share <code className="px-1.5 py-0.5 bg-slate-100 rounded font-mono">{origin}/demo</code> to start the funnel.
            </div>
          </div>
        )}

        {!loading && data?.top_refs && data.top_refs.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="funnel-refs-table">
              <thead>
                <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="pb-2 pr-3">Ref code</th>
                  <th className="pb-2 pr-3 text-right">Demos</th>
                  <th className="pb-2 pr-3 text-right">Signups</th>
                  <th className="pb-2 pr-3 text-right">Signup rate</th>
                  <th className="pb-2 pr-3 text-right">First Closers</th>
                  <th className="pb-2 pr-3 text-right">Closer rate</th>
                  <th className="pb-2" />
                </tr>
              </thead>
              <tbody>
                {data.top_refs.map((row) => (
                  <tr
                    key={row.ref_code}
                    className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`funnel-row-${row.ref_code}`}
                  >
                    <td className="py-2.5 pr-3">
                      <span className={`inline-flex items-center gap-1 font-mono text-[12px] font-bold px-2 py-0.5 rounded ${
                        row.ref_code === "DIRECT"
                          ? "bg-slate-100 text-slate-700"
                          : "bg-amber-100 text-amber-900"
                      }`}>
                        {row.ref_code}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 text-right tabular-nums font-semibold">{row.demos}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{row.signups}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums text-slate-600">{fmtPct(row.signup_rate)}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums font-semibold text-emerald-700">{row.first_closers}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums text-slate-600">{fmtPct(row.first_closer_rate)}</td>
                    <td className="py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => copyLink(row.ref_code)}
                        className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
                        data-testid={`funnel-copy-${row.ref_code}`}
                      >
                        {copied === row.ref_code ? <Check size={11} className="text-emerald-600" /> : <Copy size={11} />}
                        {copied === row.ref_code ? "Copied" : "Copy link"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="text-[11px] text-slate-400 leading-relaxed">
        Signups are matched to demos by ref code (`signup_ref_code` on the user doc).
        DIRECT = testers who landed on /demo without a ref. Numbers scoped to the
        selected window; refresh the page to re-pull.
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, tint, label, value, sub, testid }) {
  const TINTS = {
    amber:   { bg: "from-amber-500 to-amber-700",     ring: "border-amber-200" },
    sky:     { bg: "from-sky-500 to-sky-700",         ring: "border-sky-200" },
    emerald: { bg: "from-emerald-500 to-emerald-700", ring: "border-emerald-200" },
  };
  const t = TINTS[tint] || TINTS.amber;
  return (
    <div className={`card p-5 border ${t.ring}`} data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${t.bg} text-white flex items-center justify-center shrink-0`}>
          <Icon size={20} />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
            {label}
          </div>
          <div className="font-display font-black text-3xl tracking-tight text-slate-900 tabular-nums mt-0.5">
            {value}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
        </div>
      </div>
    </div>
  );
}
